import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import biApi from "../../../../../../api/biApi";
import type { BiAgentClarification, BiAgentMessage, BiAgentUiResponse } from "../../../../../../api/types/biAgentTypes";
import type {
  PivotConfiguration,
  ReportConfiguration,
  TabularConfiguration,
} from "../../../../../../api/types/svTypes";
import { ReportType } from "../../../../../../api/types/svTypes";

interface UseBiAgentChatProps {
  currentConfiguration?: ReportConfiguration;
  currentReportType?: ReportType;
  currentDataSetId?: string;
  onApplyConfiguration?: (configuration: ReportConfiguration, reportType?: ReportType, dataSetId?: string) => void;
}

interface AppliedState {
  configuration: ReportConfiguration;
  reportType?: ReportType;
  dataSetId?: string;
}

interface UseBiAgentChatResult {
  messages: BiAgentMessage[];
  isLoading: boolean;
  statusMessage: string;
  appliedMessages: Map<string, AppliedState>;
  sendMessage: (message: string) => Promise<void>;
  selectOption: (option: string) => Promise<void>;
  applyConfiguration: (
    messageId: string,
    configuration: ReportConfiguration,
    reportType?: ReportType,
    dataSetId?: string
  ) => void;
  revertConfiguration: (messageId: string) => void;
  clearChat: () => void;
}

const DEFAULT_STATUS = "Thinking...";
const STATUS_RESET_DELAY_MS = 3000;

const isPivotConfiguration = (configuration: ReportConfiguration): configuration is PivotConfiguration => {
  return "rows" in configuration && "columns" in configuration && "values" in configuration;
};

const isTabularConfiguration = (configuration: ReportConfiguration): configuration is TabularConfiguration => {
  return "fields" in configuration && "grouping" in configuration;
};

export const useBiAgentChat = ({
  currentConfiguration,
  currentReportType,
  currentDataSetId,
  onApplyConfiguration,
}: UseBiAgentChatProps = {}): UseBiAgentChatResult => {
  const [messages, setMessages] = useState<BiAgentMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState(DEFAULT_STATUS);
  const [appliedMessages, setAppliedMessages] = useState<Map<string, AppliedState>>(() => new Map());
  const chatSessionIdRef = useRef<string | undefined>(undefined);
  const abortControllerRef = useRef<AbortController | null>(null);
  const statusResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentConfigurationRef = useRef<ReportConfiguration | undefined>(currentConfiguration);
  useEffect(() => {
    currentConfigurationRef.current = currentConfiguration;
  }, [currentConfiguration]);

  const updateStatus = useCallback((msg: string) => {
    setStatusMessage(msg);
    if (statusResetTimerRef.current) clearTimeout(statusResetTimerRef.current);
    statusResetTimerRef.current = setTimeout(() => setStatusMessage(DEFAULT_STATUS), STATUS_RESET_DELAY_MS);
  }, []);

  const resetStatus = useCallback(() => {
    if (statusResetTimerRef.current) clearTimeout(statusResetTimerRef.current);
    setStatusMessage(DEFAULT_STATUS);
  }, []);

  const addMessage = useCallback(
    (
      role: "user" | "assistant",
      content: string,
      clarification?: BiAgentClarification,
      configuration?: ReportConfiguration,
      reportType?: ReportType,
      dataSetId?: string,
      executionTimeMs?: number
    ) => {
      const newMessage: BiAgentMessage = {
        id: crypto.randomUUID(),
        role,
        content,
        timestamp: new Date(),
        clarification,
        configuration,
        reportType,
        dataSetId,
        executionTimeMs,
      };
      setMessages((prev) => [...prev, newMessage]);
      return newMessage;
    },
    []
  );

  const handleResponse = useCallback(
    (response: BiAgentUiResponse | null | undefined, executionTimeMs: number) => {
      if (response?.success) {
        const content = response.notes || "Configuration generated successfully.";
        const preferredConfiguration =
          response.reportType === ReportType.Pivot ? response.pivotConfiguration : response.tabularConfiguration;
        const responseConfiguration =
          preferredConfiguration ?? response.tabularConfiguration ?? response.pivotConfiguration;
        const resolvedReportType =
          response.reportType ??
          (responseConfiguration && isPivotConfiguration(responseConfiguration)
            ? ReportType.Pivot
            : responseConfiguration && isTabularConfiguration(responseConfiguration)
              ? ReportType.Tabular
              : undefined);

        addMessage(
          "assistant",
          content,
          undefined,
          responseConfiguration ?? undefined,
          resolvedReportType,
          response.dataSetId,
          executionTimeMs
        );

        if (response.clarification) {
          addMessage("assistant", response.clarification.question, response.clarification);
        }
      } else {
        if (response?.clarification) {
          addMessage(
            "assistant",
            response.clarification.question,
            response.clarification,
            undefined,
            undefined,
            undefined,
            executionTimeMs
          );
        } else {
          addMessage(
            "assistant",
            response?.notes || "Sorry, I encountered an error processing your request.",
            undefined,
            undefined,
            undefined,
            undefined,
            executionTimeMs
          );
        }
      }
    },
    [addMessage]
  );

  const sendMessage = useCallback(
    async (message: string) => {
      if (isLoading) return;

      addMessage("user", message);
      setIsLoading(true);

      abortControllerRef.current = new AbortController();
      chatSessionIdRef.current ??= crypto.randomUUID();
      const startTime = performance.now();

      try {
        const request = {
          messageUserInput: message,
          chatSessionId: chatSessionIdRef.current,
          currentReportType,
          currentTabularConfiguration:
            currentReportType === ReportType.Tabular &&
            currentConfiguration &&
            isTabularConfiguration(currentConfiguration)
              ? currentConfiguration
              : undefined,
          currentPivotConfiguration:
            currentReportType === ReportType.Pivot && currentConfiguration && isPivotConfiguration(currentConfiguration)
              ? currentConfiguration
              : undefined,
        };

        for await (const event of biApi.biAgentUi.sendMessageStream(request, abortControllerRef.current.signal)) {
          if (event.type === "progress") {
            updateStatus(event.message);
          } else if (event.type === "result") {
            const executionTimeMs = Math.round(performance.now() - startTime);
            handleResponse(event.data, executionTimeMs);
          }
        }
      } catch (error) {
        if (axios.isCancel(error) || (error instanceof Error && error.name === "AbortError")) {
          return;
        }
        const executionTimeMs = Math.round(performance.now() - startTime);
        addMessage(
          "assistant",
          "Sorry, an unexpected error occurred. Please try again.",
          undefined,
          undefined,
          undefined,
          undefined,
          executionTimeMs
        );
      } finally {
        setIsLoading(false);
        resetStatus();
        abortControllerRef.current = null;
      }
    },
    [isLoading, currentConfiguration, currentReportType, addMessage, handleResponse, updateStatus, resetStatus]
  );

  const selectOption = useCallback(
    async (option: string) => {
      await sendMessage(option);
    },
    [sendMessage]
  );

  const applyConfiguration = useCallback(
    (messageId: string, configuration: ReportConfiguration, reportType?: ReportType, dataSetId?: string) => {
      const configBeforeApply = currentConfigurationRef.current;
      if (configBeforeApply) {
        setAppliedMessages((prev) =>
          new Map(prev).set(messageId, {
            configuration: configBeforeApply,
            reportType: currentReportType,
            dataSetId: currentDataSetId,
          })
        );
      }
      onApplyConfiguration?.(configuration, reportType, dataSetId);
    },
    [currentReportType, currentDataSetId, onApplyConfiguration]
  );

  const revertConfiguration = useCallback(
    (messageId: string) => {
      const previousState = appliedMessages.get(messageId);
      if (previousState) {
        onApplyConfiguration?.(previousState.configuration, previousState.reportType, previousState.dataSetId);
        setAppliedMessages((prev) => {
          const newMap = new Map(prev);
          newMap.delete(messageId);
          return newMap;
        });
      }
    },
    [appliedMessages, onApplyConfiguration]
  );

  const clearChat = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    resetStatus();
    setMessages([]);
    setAppliedMessages(new Map());
    chatSessionIdRef.current = undefined;
  }, [resetStatus]);

  return {
    messages,
    isLoading,
    statusMessage,
    appliedMessages,
    sendMessage,
    selectOption,
    applyConfiguration,
    revertConfiguration,
    clearChat,
  };
};
